/**
 * InvoiceReceivablesCheckDialog — Sales Control › Invoice › Receivables Check.
 * Converted 1:1 from Access `frmCtrlSalesQryInvoiceAdd_ReceivablesCheck`.
 *
 * The Access form is read-mostly: 7 editable `rc_*` fields + ~20 LOCKED readonly
 * context cells that DLookUp from `public_v_invoice_scs` by `customs_inv_no`. All
 * readonly values are already present on the list `SaleInvoice` row (same view),
 * so the parent passes the selected row in — no extra fetch needed.
 *
 * Access layout (2-col grid; left Left=4332 / right Left=12984), by section/Top:
 *   Receivables Check
 *     Row1  B/L No (RO)                | ETA/ATA (edit, date)
 *     Row2  Docs Sent Date (RO, date)  | Tracking No. (edit)
 *     Row3  Docs Delivered Date (edit) | Due Date (edit, date)
 *     Row4  Paid Date (edit, date)     | Paid Amount (edit, Standard)
 *     Row5  Paid Details (edit)
 *   Contract Info
 *     Row1  Contract Ref (RO)          | Buyer's Name (RO)
 *   Invoice Info
 *     Row1  ID (RO)
 *     Row2  To bank (RO)
 *     Row3  No. INV (RO)               | No. VAT (RO)
 *     Row4  For DO (RO)
 *     Row5  Customs INV No (RO)        | Customer Invoice Number (RO)
 *     Row6  Invoice Type (RO)          | Invoice Date (RO, date)
 *     Row7  No bag (RO, Format 0)
 *   Invoice Info (customer values, all Standard / date)
 *     Row1  Customer INV GW (RO)       | Customer INV NW (RO)
 *     Row2  Price (RO)                 | Total Value 2 (RO)
 *     Row3  Adjusted Value (RO)        | ATD (RO, date)
 * No required fields / validation (Access btnSave has no strCheck / errCheck).
 */
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SaleInvoice,
  type SaleInvoiceReceivablesWrite,
  useUpdateSaleInvoiceReceivablesMutation,
} from '../../store/api/saleControlApi'
import { FORM_GRID_3COL } from '../../theme/responsive'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  row: SaleInvoice | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  rcEtaAta: string
  rcTrackingNo: string
  rcDocsDeliveredDate: string
  rcDueDate: string
  rcPaidDate: string
  rcPaidAmount: string
  rcPaidDetails: string
}

const EMPTY: Draft = {
  rcEtaAta: '', rcTrackingNo: '', rcDocsDeliveredDate: '', rcDueDate: '',
  rcPaidDate: '', rcPaidAmount: '', rcPaidDetails: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionSx: SxProps<Theme> = {
  gridColumn: '1 / -1', mt: 0.5, fontWeight: 700, color: 'text.secondary',
  fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4,
}
// Readonly (Access Locked) cells → muted background to signal non-editable.
const roSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { bgcolor: 'action.hover' },
  '& .MuiInputBase-input': { color: 'text.secondary' },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard" (`#,##0.00`) — 2 decimals + thousand separator, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
/** Access Format="0" — integer, no decimals, no thousand separator. */
function fmtInt(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return String(Math.round(n))
}
/** Access Format="dd-mmm-yy" → DD-MMM-YYYY (fall back to raw text if unparseable). */
function fmtDate(v: unknown): string {
  if (v == null || v === '') return ''
  const d = dayjs(String(v))
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}
const s = (v: unknown): string => (v == null ? '' : String(v))

export default function InvoiceReceivablesCheckDialog({ open, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [updateReceivables, { isLoading: saving }] = useUpdateSaleInvoiceReceivablesMutation()

  const invoiceId = row?.id ?? null

  // Hydrate editable rc_* from the selected row on open.
  useEffect(() => {
    if (!open || !row) { setDraft(EMPTY); return }
    setDraft({
      rcEtaAta: s(row.rcEtaAta).slice(0, 10),
      rcTrackingNo: s(row.rcTrackingNo),
      rcDocsDeliveredDate: s(row.rcDocsDeliveredDate).slice(0, 10),
      rcDueDate: s(row.rcDueDate).slice(0, 10),
      rcPaidDate: s(row.rcPaidDate).slice(0, 10),
      rcPaidAmount: fmtStd(row.rcPaidAmount),
      rcPaidDetails: s(row.rcPaidDetails),
    })
  }, [open, row])

  const canSave = canEdit && !!invoiceId && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave || !invoiceId) return
    const payload: SaleInvoiceReceivablesWrite = {
      rcEtaAta: draft.rcEtaAta || null,
      rcTrackingNo: draft.rcTrackingNo || null,
      rcDocsDeliveredDate: draft.rcDocsDeliveredDate || null,
      rcDueDate: draft.rcDueDate || null,
      rcPaidDate: draft.rcPaidDate || null,
      rcPaidAmount: parseNum(draft.rcPaidAmount),
      rcPaidDetails: draft.rcPaidDetails || null,
    }
    try {
      await updateReceivables({ id: invoiceId, body: payload }).unwrap()
      snack.success('Receivables updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update receivables failed'))
    }
  }

  // ── Field renderers ────────────────────────────────────────────────────────
  const editDate = (key: keyof Draft, label: string, tabIndex: number) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      disabled={!canEdit}
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true }, inputProps: { tabIndex } } }}
    />
  )
  const editText = (key: keyof Draft, label: string, tabIndex: number) => (
    <TextField
      label={label} size={SM} fullWidth value={draft[key] as string} disabled={!canEdit}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      inputProps={{ tabIndex }}
    />
  )
  const roText = (label: string, value: string, align?: 'right') => (
    <TextField
      label={label} size={SM} fullWidth value={value}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
      inputProps={align ? { style: { textAlign: align } } : undefined}
      sx={roSx}
    />
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>Receivables Check</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit receivables.</Alert>}
        {/* Toàn form → 3 cột (auto-flow theo thứ tự field). */}
        <Box sx={FORM_GRID_3COL}>
          {/* ── Receivables Check ─────────────────────────────────────────── */}
          <Typography component="div" sx={sectionSx}>Receivables Check</Typography>
          {roText('B/L No', s(row?.blNumber))}
          {roText('Docs Sent Date', fmtDate(row?.docsPresentationDate))}
          {editDate('rcEtaAta', 'ETA/ATA', 1)}
          {editText('rcTrackingNo', 'Tracking No.', 3)}
          {editDate('rcDocsDeliveredDate', 'Docs Delivered Date', 4)}
          {editDate('rcDueDate', 'Due Date', 5)}
          {editDate('rcPaidDate', 'Paid Date', 6)}
          <TextField
            label="Paid Amount" size={SM} fullWidth type="text" value={draft.rcPaidAmount} disabled={!canEdit}
            onChange={(e) => set('rcPaidAmount', e.target.value)}
            onBlur={() => set('rcPaidAmount', fmtStd(draft.rcPaidAmount))}
            inputProps={{ ...numProps, tabIndex: 7 }}
          />
          {editText('rcPaidDetails', 'Paid Details', 8)}

          {/* ── Contract Info ─────────────────────────────────────────────── */}
          <Typography component="div" sx={sectionSx}>Contract Info</Typography>
          {roText('Contract Ref', s(row?.contractRef))}
          <Box sx={{ gridColumn: { md: 'span 2' } }}>{roText("Buyer's Name", s(row?.buyerName))}</Box>

          {/* ── Invoice Info ──────────────────────────────────────────────── */}
          <Typography component="div" sx={sectionSx}>Invoice Info</Typography>
          {roText('ID', s(row?.id))}
          {roText('To bank', s(row?.toBank))}
          {roText('No. INV', s(row?.invoiceNo))}
          {roText('No. VAT', s(row?.vatNo))}
          {roText('For DO', s(row?.lstDoNo))}
          {roText('No bag', fmtInt(row?.noBag), 'right')}
          {roText('Customs INV No', s(row?.customsInvNo))}
          {roText('Customer Invoice Number', s(row?.customerInvoiceNumber))}
          {roText('Invoice Type', s(row?.invoiceType))}
          {roText('Invoice Date', fmtDate(row?.invoiceDate))}

          {/* ── Invoice Info (customer values) ────────────────────────────── */}
          <Typography component="div" sx={sectionSx}>Invoice Info</Typography>
          {roText('Customer INV GW', fmtStd(row?.customerInvGw), 'right')}
          {roText('Customer INV NW', fmtStd(row?.customerInvNw), 'right')}
          {roText('Price', fmtStd(row?.customerInvPrice), 'right')}
          {roText('Total Value 2', fmtStd(row?.customerInvTotal), 'right')}
          {roText('Adjusted Value', fmtStd(row?.customerInvAdjust), 'right')}
          {roText('ATD', fmtDate(row?.customerInvAtd))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
